import { Box, Skeleton, Stack, Text } from "@mantine/core";
import { useOpcdCodingResults } from "../../../hooks/queries";
import { useAppSelector } from "../../../store/hooks";
import { encoderErrorMessage } from "../../../utils/encoder";
import {
  codingResultHasCodes,
  currentCodingResult,
} from "../../../utils/pro-fee-coding-results";
import PollingRefreshIndicator from "../../Shared/PollingRefreshIndicator";
import CodingResultsTables from "./CodeResultsTables";

const OpcdCodingResults: React.FC = () => {
  const mrn = useAppSelector((state) => state.fhir.mrn);
  const { data, isLoading, isFetching, isError, error } = useOpcdCodingResults({
    polling: true,
  });
  const result = currentCodingResult(data);
  const hasCodes = codingResultHasCodes(result);
  const requestError =
    data && data.success === false
      ? data.error || "Unable to load coding results"
      : isError
        ? encoderErrorMessage(error, "Unable to load coding results")
        : "";

  if (isLoading) {
    return (
      <Stack p="md" gap="sm">
        <Skeleton height={42} radius="md" />
        <Skeleton height={48} radius="md" />
        <Skeleton height={48} radius="md" />
      </Stack>
    );
  }

  if (!mrn) {
    return (
      <Box p="xl">
        <Text c="dimmed" ta="center">
          Encounter MRN is required to load coding results.
        </Text>
      </Box>
    );
  }

  if (requestError && !hasCodes) {
    return (
      <Box p="xl">
        <Text c="red" ta="center">
          {requestError}
        </Text>
      </Box>
    );
  }

  return (
    <Stack
      p="sm"
      gap="sm"
      style={{ height: "100%", minHeight: 0, overflow: "hidden" }}
    >
      {requestError ? (
        <Text size="sm" c="red" role="alert">
          {requestError}
        </Text>
      ) : null}

      <Box
        style={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          style={{
            pointerEvents: "none",
            position: "absolute",
            right: 12,
            top: 8,
            zIndex: 10,
          }}
        >
          <PollingRefreshIndicator visible={isFetching && !isLoading} />
        </Box>
        <Box style={{ overflow: "auto", flex: 1, minHeight: 0 }}>
          {!hasCodes || !result ? (
            <Box p="xl">
              <Text c="dimmed" ta="center">
                Coding is not finalized for this encounter.
              </Text>
            </Box>
          ) : (
            <CodingResultsTables result={result} />
          )}
        </Box>
      </Box>
    </Stack>
  );
};

export default OpcdCodingResults;
